import { formatCurrency } from 'common/FormatUtilities';
import React from 'react';
import { TextReplace } from 'src/shared/design-system/Utilities';
import { getPercentFromDecimal } from 'src/shared/utils/NumberUtils';
import {
  BREAKDOWN_DISCOUNTED_DELIVERY_TEXT,
  BREAKDOWN_DISCOUNT_TEXT,
  BREAKDOWN_DISCOUNT_YOUR_ORDER_TEXT,
  BREAKDOWN_PERCENT_OFF_TEXT,
  BREAKDOWN_SAVINGS_ICON,
  BREAKDOWN_SAVINGS_TEXT,
  DISCOUNTED_DELIVERY_TEXT,
} from '../consts';
import { useAnimatedNumberWithAnimatedState } from '../hooks';
import { BulkDiscountBrekadownProps } from '../types';
import {
  CompletedIcon,
  Container,
  DeliverySavingsText,
  DiscountLine,
  DiscountText,
  SavingsIconContainer,
  SpendText,
  TotalSavings,
  Wrapper,
} from './styles';

export const BulkDiscountBreakdown: React.FC<BulkDiscountBrekadownProps> = (props) => {
  const { discountedDeliveryFee, hasFreeDeliveryPromo, showFreeDelivery, tiers, total, totalSavings = 0 } = props;

  const { animatedNumber: animatedTotalSavings, isAnimating } = useAnimatedNumberWithAnimatedState(totalSavings);
  const content = tiers.map(({ discountRate, minSubtotal }, index) => {
    const subtotal = Math.max(0, minSubtotal);
    const isFreeDelivery = showFreeDelivery && discountRate === 0 && index === 0;
    const discountText = isFreeDelivery ? (
      <TextReplace
        replaceText={[discountedDeliveryFee === 0 ? 'Free' : formatCurrency(discountedDeliveryFee)]}
        text={BREAKDOWN_DISCOUNTED_DELIVERY_TEXT}
      />
    ) : (
      ` ${getPercentFromDecimal(discountRate).toFixed(0)}${BREAKDOWN_PERCENT_OFF_TEXT}`
    );
    const isCompleted = total >= minSubtotal;

    return (
      <DiscountLine key={`${subtotal}-${index}`}>
        <CompletedIcon show={isCompleted} />
        {hasFreeDeliveryPromo && isFreeDelivery ? (
          <DiscountText>
            {discountedDeliveryFee === 0 ? 'Free' : formatCurrency(discountedDeliveryFee)} {DISCOUNTED_DELIVERY_TEXT}
          </DiscountText>
        ) : (
          <TextReplace
            replaceText={[
              <SpendText key={`spend-text-${index}`}>{formatCurrency(subtotal)}</SpendText>,
              <DiscountText key={`discount-text-${index}`}>{discountText}</DiscountText>,
              isFreeDelivery ? '' : BREAKDOWN_DISCOUNT_YOUR_ORDER_TEXT,
            ]}
            text={BREAKDOWN_DISCOUNT_TEXT}
          />
        )}
      </DiscountLine>
    );
  });

  return (
    <Container>
      <Wrapper>{content}</Wrapper>
      <TotalSavings show={!!totalSavings}>
        <TextReplace
          replaceText={[
            <SavingsIconContainer key={'savings-icon'} animate={isAnimating}>
              {BREAKDOWN_SAVINGS_ICON}
            </SavingsIconContainer>,
            <React.Fragment key={`savings-text`}>
              <DiscountText>
                {animatedTotalSavings.val.to((value) => (value === totalSavings ? '' : formatCurrency(value, false)))}
              </DiscountText>
              <DeliverySavingsText>{formatCurrency(totalSavings, false)}</DeliverySavingsText>
            </React.Fragment>,
          ]}
          text={BREAKDOWN_SAVINGS_TEXT}
        />
      </TotalSavings>
    </Container>
  );
};
